/**
 * Builds the external-trip payload for a leg being confirmed, straight from
 * the stored `legs` row.
 *
 * The form path had to convert local-naive form times into UTC first; stored
 * leg rows already hold UTC, so this builder is a plain mapping. Kept pure and
 * separate so the confirm flow can be unit-tested without EAPI or the DB.
 */
import { mapLegToCreateTrip, type EapiCreateTripBody } from "@/lib/eapi-trip-mapper";
import { isNoEapiState } from "@/lib/eapi-eligibility";
import type { PromoteTripRequest } from "@/lib/promote-order-trips";
import type { Location, Tractor } from "@/types/tms";

/** The `legs` columns the payload needs. */
export interface ConfirmTripLegRow {
  leg_sequence: number;
  ship_from_street_address: string;
  ship_from_city: string;
  ship_from_province_state?: string | null;
  ship_from_postal_zip_code?: string | null;
  ship_from_country?: string | null;
  ship_from_location_id?: string | null;
  ship_to_street_address: string;
  ship_to_city: string;
  ship_to_province_state?: string | null;
  ship_to_postal_zip_code?: string | null;
  ship_to_country?: string | null;
  ship_to_location_id?: string | null;
  ready_time: string;
  ready_time_end?: string | null;
  requested_delivery_time: string;
  requested_delivery_time_end?: string | null;
}

export interface BuildConfirmTripArgs {
  leg: ConfirmTripLegRow;
  parentUniqueId: string;
  tractor?: Tractor | null;
  trailerType?: { kind: string; length_ft: number } | null;
  tripType?: string | null;
  locations?: Location[] | null;
  customFields?: Parameters<typeof mapLegToCreateTrip>[5];
  tenantSlug?: string | null;
  /** Total legs on the order; `-L<n>` naming only when > 1. */
  legCount?: number;
}

/**
 * Returns the create-trip request for this leg, or null when the leg is
 * local-only (Manual trips, and bobtail where the tenant has it disabled) and
 * must never produce an external trip.
 */
export function buildConfirmTripRequest(args: BuildConfirmTripArgs): PromoteTripRequest | null {
  const { leg, parentUniqueId, tractor, trailerType, tripType, locations, tenantSlug } = args;

  if (isNoEapiState(trailerType?.kind ?? null, tripType ?? null, tenantSlug)) return null;

  const tripBody: EapiCreateTripBody = mapLegToCreateTrip(
    {
      leg_sequence: leg.leg_sequence,
      ship_from_street_address: leg.ship_from_street_address,
      ship_from_city: leg.ship_from_city,
      ship_from_province_state: leg.ship_from_province_state ?? null,
      ship_from_postal_zip_code: leg.ship_from_postal_zip_code ?? null,
      ship_from_country: leg.ship_from_country ?? null,
      ship_from_location_id: leg.ship_from_location_id ?? null,
      ship_to_street_address: leg.ship_to_street_address,
      ship_to_city: leg.ship_to_city,
      ship_to_province_state: leg.ship_to_province_state ?? null,
      ship_to_postal_zip_code: leg.ship_to_postal_zip_code ?? null,
      ship_to_country: leg.ship_to_country ?? null,
      ship_to_location_id: leg.ship_to_location_id ?? null,
      ready_time: leg.ready_time,
      ready_time_end: leg.ready_time_end ?? leg.ready_time,
      requested_delivery_time: leg.requested_delivery_time,
      requested_delivery_time_end:
        leg.requested_delivery_time_end ?? leg.requested_delivery_time,
    },
    parentUniqueId,
    tractor ?? null,
    trailerType ?? null,
    locations ?? null,
    args.customFields ?? null,
    args.legCount ?? 1,
  );

  return {
    legSequence: leg.leg_sequence,
    tripBody,
    label: `confirm leg ${leg.leg_sequence}`,
    assert: {
      flow: "confirm",
      parentUniqueId,
      legSequence: leg.leg_sequence,
    },
  };
}
